import React, { useState } from 'react';
import { ShoppingCartOutlined } from '@ant-design/icons';
import { Button } from '../../../ui/primitives/Button';
import { Modal } from '../../../ui/primitives/Modal';
import { useMember } from '../../../common/member/MemberContext';
import { MemberProfile } from '../../../model/members/MemberProfile';
import { PosScreenPermissions } from '../../../model/auth/Permissions';
import PosTerminal from '../../pointofsale/PosTerminal';

interface PosTransactionModalProps {
  member: MemberProfile;
  /** Fired after a sale is processed, so the profile can refresh its POS list. */
  onTransactionCompleted?: () => void;
  /** Full-width trigger for the Member Profile V2 "Quick actions" rail. */
  block?: boolean;
  /**
   * Identifies this trigger. The profile renders the same action twice — once in
   * the identity banner, once in the quick-actions rail — so tests need to be
   * able to address a specific one rather than matching on the shared label.
   */
  triggerTestId?: string;
}

/**
 * Sell POS items to the member being viewed without leaving their profile: the
 * same till as /pos, opened in a modal with this member already selected.
 *
 * Only rendered for users who can reach the Point of Sale screen itself — the
 * gate is the shared PosScreenPermissions list, so it cannot drift from the
 * route's. Hidden before the member exists (the blank create form), since a
 * transaction needs a membership ID.
 */
const PosTransactionModal: React.FC<PosTransactionModalProps> = ({
  member,
  onTransactionCompleted,
  block = false,
  triggerTestId,
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { permissions } = useMember();

  const canUsePointOfSale = PosScreenPermissions.some(required =>
    permissions?.permissions?.some(p => p.name === required)
  );

  if (!canUsePointOfSale || !member.membershipId) {
    return null;
  }

  return (
    <>
      <Button
        variant="primary"
        block={block}
        data-testid={triggerTestId}
        icon={<ShoppingCartOutlined />}
        onClick={() => setIsModalVisible(true)}
      >
        Point of Sale
      </Button>

      <Modal
        title={`Point of Sale — ${member.name}`}
        open={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        width={1100}
      >
        <PosTerminal
          presetMember={member}
          allowItemManagement={false}
          onTransactionComplete={onTransactionCompleted}
        />
      </Modal>
    </>
  );
};

export default PosTransactionModal;
